import React from 'react';
import {useTranslation} from 'react-i18next';
import {useHistory, useLocation} from 'react-router-dom';
import {PATHS} from 'Routes';
import {GUEST_APP_EVENTS_NAME} from 'utils/constants';
import {useDocumentSelfieDetection} from '../../../hooks';
import {useComputedReservationDetails} from 'context/computedReservationDetails';
import {useErrorModal, useModalControls} from 'utils/hooks';
import {BackLocationStateType} from 'views/VerifyModalScreen/AddPersonalDataScanScreen/AddPersonalDataScanScreen';
import {
  LocationStates,
  PhotoLocationStateType,
} from 'views/VerifyModalScreen/VerifyModalScreen';
import femaleIcon from 'assets/female.svg';
import scanningIcon from 'assets/scanning_icn.svg';
import scanningErrorIcon from 'assets/scanning_doc_error.svg';
import Modal from 'components/Modal';
import {ModalSecondaryButton} from 'styled/common';
import {ConfirmIdentityTemplate} from 'components/ConfirmIdentity';
import useMixPanel from 'hooks/useMixPanel';

export type SelfieLocationStateType = BackLocationStateType & {
  [LocationStates.selfiePhoto]?: PhotoLocationStateType & {result?: any};
  isVerifiedOrRetake: boolean;
};

function SelfieIdentity() {
  const {t} = useTranslation();
  const history = useHistory();
  const location = useLocation<SelfieLocationStateType>();
  const {ErrorModal, displayError} = useErrorModal();
  const reservationDetails = useComputedReservationDetails();
  const {
    error,
    status,
    resetDetection,
    DetectionErrorModal,
    startSelfieFaceDetection,
    startSelfieAndDocumentFacesComparing,
  } = useDocumentSelfieDetection();
  const preloadedPhoto = location.state?.[LocationStates.selfiePhoto]?.picture;

  const {
    isOpen: isDetectingModalOpen,
    openModal: openDetectingModal,
    closeModal: closeDetectingModal,
  } = useModalControls();
  const {
    isOpen: isComparingModalOpen,
    openModal: openComparingModal,
    closeModal: closeComparingModal,
  } = useModalControls();
  const {IdSelfieVerification} = useMixPanel();
  // const areBiomatchRetriesEnabled =
  //   reservationDetails.reservation.are_biomatch_retries_enabled;

  const goBack = React.useCallback(() => {
    history.push(PATHS.verificationDocumentFront, location.state);
  }, [history, location.state]);

  React.useEffect(() => {
    const hasPersistedState = location.state;

    if (!hasPersistedState) {
      goBack();
    }
  }, [goBack, location.state]);

  const handleError = React.useCallback(() => {
    const params = {
      status: 'Error',
    };
    IdSelfieVerification(GUEST_APP_EVENTS_NAME.IDVerificationResult, params);
    closeDetectingModal();
    closeComparingModal();
  }, [IdSelfieVerification, closeComparingModal, closeDetectingModal]);

  const getPersistedState = React.useCallback(
    (selfiePhoto: string, data: any): SelfieLocationStateType => {
      const checkId = data?.selfieCheckId || '';
      const isPassed = data.is_match;

      return {
        ...location.state,
        isVerifiedOrRetake: true,
        [LocationStates.selfiePhoto]: {
          result: data,
          picture: selfiePhoto || '',
          checkId,
        },
        [LocationStates.identityVerificationState]: {
          biomatchPassed: isPassed,
          isIdentityVerificationFinished: typeof isPassed !== 'undefined',
        },
      };
    },
    [location.state],
  );

  const handleOCRMatchingCompletion = React.useCallback(
    (image: string, data: any) => {
      const params = {
        status: data?.is_match,
        distance: data.distance,
      };
      IdSelfieVerification(GUEST_APP_EVENTS_NAME.IDVerificationResult, params);
      let persistedState = getPersistedState(image, data);
      closeComparingModal();
      // if (!data?.is_match) {
      //   const failedAttempts = persistedState?.selfiePhoto?.failedOCRAttempts || 0;
      //   persistedState = {
      //     ...persistedState,
      //     selfiePhoto: {
      //       ...persistedState.selfiePhoto,
      //       checkId: persistedState?.selfiePhoto?.checkId || '',
      //       picture: persistedState?.selfiePhoto?.picture || '',
      //       failedOCRAttempts: areBiomatchRetriesEnabled ? failedAttempts + 1 : 0,
      //     },
      //   };
      // }

      history.push(PATHS.verificationResult, persistedState);
    },
    [IdSelfieVerification, closeComparingModal, getPersistedState, history],
  );

  const handleSuccessSendSelfie = React.useCallback(
    async (image: string, data: any) => {
      closeDetectingModal();
      openComparingModal();
      const selfieCheckId = data?.id;
      await startSelfieAndDocumentFacesComparing({
        selfieCheckId,
        image,
        onSuccess: handleOCRMatchingCompletion,
        onError: handleError,
      });
    },
    [
      closeDetectingModal,
      handleError,
      handleOCRMatchingCompletion,
      openComparingModal,
      startSelfieAndDocumentFacesComparing,
    ],
  );

  const sendScreenshot = React.useCallback(
    async (screenshot: string) => {
      try {
        if (!screenshot) return;
        const reservationId = reservationDetails?.reservation?.id as string;
        openDetectingModal();
        await startSelfieFaceDetection({
          image: screenshot,
          reservationId,
          onSuccess: handleSuccessSendSelfie,
          onError: handleError,
        });
      } catch (err) {
        displayError(err);
      }
    },
    [
      displayError,
      handleError,
      handleSuccessSendSelfie,
      openDetectingModal,
      reservationDetails?.reservation?.id,
      startSelfieFaceDetection,
    ],
  );

  const handleCancel = React.useCallback(() => {
    resetDetection();
    closeDetectingModal();
    closeComparingModal();
  }, [closeComparingModal, closeDetectingModal, resetDetection]);

  return (
    <>
      <ConfirmIdentityTemplate
        noUpload
        detailsText={t('fit_your_face_inside_the_camera')}
        stateName={LocationStates.selfiePhoto}
        status={status}
        onSubmit={sendScreenshot}
        error={error}
        preloadedScreenshot={preloadedPhoto}
      />
      <DetectionErrorModal />
      <ErrorModal iconSrc={scanningErrorIcon} />
      <Modal
        open={isComparingModalOpen}
        title={t('comparing')}
        text={t('we_are_comparing_doc_and_selfie')}
        iconSrc={scanningIcon}
        iconAlt="Scanning female"
      >
        <ModalSecondaryButton onClick={handleCancel}>{t('cancel')}</ModalSecondaryButton>
      </Modal>
      <Modal
        open={isDetectingModalOpen}
        title={t('detecting')}
        text={t('we_are_detecting_your_face')}
        iconSrc={femaleIcon}
        iconAlt="Female"
      >
        <ModalSecondaryButton onClick={handleCancel}>{t('cancel')}</ModalSecondaryButton>
      </Modal>
    </>
  );
}

export {SelfieIdentity};
